import React, { useState, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { 
  ArrowUpDown, 
  Filter, 
  Share2, 
  Users,
  DollarSign,
  X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { supabase } from '@/integrations/supabase/client';
import SeoHead from '@/components/shared/SeoHead';
import { LeaderboardAd, InContentAd, MobileBannerAd } from '@/components/shared/AdSense';
import AdFrame from '@/components/shared/AdFrame';
import OccupationLink from '@/components/shared/OccupationLink';
import MetricNavigation from '@/components/shared/MetricNavigation';
import CSVExportButton from '@/components/shared/CSVExportButton';
import { Pagination, PaginationContent, PaginationEllipsis, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from '@/components/ui/pagination';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface RankingOccupation {
  soc_code: string;
  occ_title: string;
  median_salary: number;
  mean_salary: number;
  employment: number;
  p10_salary?: number;
  p90_salary?: number;
  no_degree_required: boolean;
  slug: string;
}

type SortField = 'median_salary' | 'mean_salary' | 'employment' | 'soc_code' | 'occ_title';
type SortDirection = 'asc' | 'desc';
type RankingView = 'highest-paying' | 'most-jobs' | 'alphabetical' | 'soc-code';

// US Income Percentile Thresholds (2024 individual income data)
const US_INCOME_THRESHOLDS = {
  top1: 400000,
  top5: 200000,
  top10: 130000,
  top25: 75000,
  median: 45000,
  bottom25: 30000
};

const getEarnerProfile = (salary: number) => {
  if (salary >= US_INCOME_THRESHOLDS.top1) {
    return { tier: 'Elite Earner', badge: '🏆 Top 1%', percentile: 99, color: 'text-amber-600' };
  }
  if (salary >= US_INCOME_THRESHOLDS.top5) {
    return { tier: 'Elite Earner', badge: '🥇 Top 5%', percentile: 95, color: 'text-amber-600' };
  }
  if (salary >= US_INCOME_THRESHOLDS.top10) {
    return { tier: 'High Earner', badge: '🥈 Top 10%', percentile: 90, color: 'text-emerald-600' };
  }
  if (salary >= US_INCOME_THRESHOLDS.top25) {
    return { tier: 'High Earner', badge: null, percentile: 75, color: 'text-emerald-600' };
  }
  if (salary >= US_INCOME_THRESHOLDS.median) {
    return { tier: 'Above Average', badge: null, percentile: 50, color: 'text-green-600' };
  }
  return { tier: 'Below Average', badge: null, percentile: 25, color: 'text-yellow-600' };
};

const OccupationRankings: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Get URL parameters
  const viewParam = (searchParams.get('view') as RankingView) || 'highest-paying';
  const sortFieldParam = searchParams.get('sort') as SortField | null;
  const sortDirParam = searchParams.get('dir') as SortDirection | null;
  const socPrefix = searchParams.get('soc') || '';
  const minSalary = searchParams.get('min_salary') || '';
  const maxSalary = searchParams.get('max_salary') || '';
  const minEmployment = searchParams.get('min_employment') || '';
  const noDegree = searchParams.get('no_degree') === 'true';
  const earnerTier = searchParams.get('earner_tier') || '';
  const currentPage = parseInt(searchParams.get('page') || '1');
  const pageSize = 50;

  const [showFilters, setShowFilters] = useState(false);

  // Derive sort from view when no explicit sort param is in URL
  const effectiveSortField: SortField = sortFieldParam || (
    viewParam === 'most-jobs' ? 'employment' :
    viewParam === 'alphabetical' ? 'occ_title' :
    viewParam === 'soc-code' ? 'soc_code' :
    'median_salary'
  );
  const effectiveSortDir: SortDirection = sortDirParam || (
    viewParam === 'alphabetical' || viewParam === 'soc-code' ? 'asc' : 'desc'
  );

  // Fetch all occupations for ranking
  const { data, isLoading, error } = useQuery({
    queryKey: ['occupation-rankings', socPrefix, minSalary, maxSalary, minEmployment, noDegree],
    queryFn: async () => {
      const body: Record<string, unknown> = {
        search: '*',
        limit: 10000,
        offset: 0
      };
      
      if (noDegree) body.no_degree = true;
      if (minSalary) body.min_salary = minSalary;
      if (maxSalary) body.max_salary = maxSalary;
      if (socPrefix) body.soc_prefix = socPrefix;
      if (minEmployment) body.min_employment = minEmployment;
      
      const { data, error } = await supabase.functions.invoke('api-occupations', {
        body
      });
      
      if (error) throw error;
      return data;
    },
    staleTime: 1000 * 60 * 30,
  });

  // Apply filters and sorting
  const filteredAndSortedData = useMemo(() => {
    if (!data?.occupations) return [];
    
    let filtered = [...data.occupations] as RankingOccupation[];
    
    // Apply SOC code prefix filter (client-side backup — server also filters)
    if (socPrefix) {
      filtered = filtered.filter(occ => occ.soc_code.startsWith(socPrefix));
    }
    
    // Apply employment filter
    if (minEmployment) {
      const minEmp = parseInt(minEmployment);
      filtered = filtered.filter(occ => (occ.employment || 0) >= minEmp);
    }
    
    // Apply earner tier filter
    if (earnerTier) {
      filtered = filtered.filter(occ => {
        const salary = occ.median_salary || occ.mean_salary || 0;
        const profile = getEarnerProfile(salary);
        if (earnerTier === 'elite') return profile.percentile >= 95;
        if (earnerTier === 'high') return profile.percentile >= 75;
        if (earnerTier === 'above') return profile.percentile >= 50;
        return true;
      });
    }
    
    // Sort data using effective sort derived from URL/view
    filtered.sort((a, b) => {
      let aVal: unknown = a[effectiveSortField];
      let bVal: unknown = b[effectiveSortField];
      
      if (effectiveSortField === 'occ_title' || effectiveSortField === 'soc_code') {
        aVal = String(aVal || '').toLowerCase();
        bVal = String(bVal || '').toLowerCase();
        return effectiveSortDir === 'asc' 
          ? (aVal as string).localeCompare(bVal as string)
          : (bVal as string).localeCompare(aVal as string);
      }
      
      // Numeric sorting
      aVal = Number(aVal) || 0;
      bVal = Number(bVal) || 0;
      return effectiveSortDir === 'asc' ? (aVal as number) - (bVal as number) : (bVal as number) - (aVal as number);
    });
    
    return filtered;
  }, [data?.occupations, socPrefix, minEmployment, earnerTier, effectiveSortField, effectiveSortDir]);

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedData.length / pageSize);
  const paginatedData = filteredAndSortedData.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  // Update URL when filters/sorting change
  const updateParams = (updates: Record<string, string | null>) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value === null || value === '') {
        newParams.delete(key);
      } else {
        newParams.set(key, value);
      }
    });
    newParams.set('page', '1'); // Reset to first page
    setSearchParams(newParams);
  };

  const handleSort = (field: SortField) => {
    const newDir = effectiveSortField === field && effectiveSortDir === 'desc' ? 'asc' : 'desc';
    updateParams({ sort: field, dir: newDir });
  };

  const handleViewChange = (newView: RankingView) => {
    // Clear explicit sort when switching views so view-default sort takes over
    const newParams = new URLSearchParams(searchParams);
    newParams.set('view', newView);
    newParams.delete('sort');
    newParams.delete('dir');
    newParams.set('page', '1');
    setSearchParams(newParams);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  const handlePageChange = (page: number) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('page', page.toString());
    setSearchParams(newParams);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleShare = () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({
        title: 'Occupation Rankings - Wage Atlas',
        text: 'Check out these occupation rankings!',
        url: url,
      }).catch(() => {
        navigator.clipboard.writeText(url);
      });
    } else {
      navigator.clipboard.writeText(url);
    }
  };

  const clearFilters = () => {
    setSearchParams({ view: viewParam });
  };

  const SortButton = ({ field, children }: { field: SortField; children: React.ReactNode }) => {
    const isActive = effectiveSortField === field;
    return (
      <button
        onClick={() => handleSort(field)}
        className={`flex items-center gap-1 font-semibold hover:text-primary transition-colors ${isActive ? 'text-primary' : ''}`}
      >
        <span className="whitespace-nowrap">{children}</span>
        <ArrowUpDown className="h-3 w-3 flex-shrink-0" />
      </button>
    );
  };

  // Mobile Card Layout Component
  const MobileCardLayout = () => (
    <div className="space-y-4">
      {/* Mobile Sort Controls */}
      <div className="flex flex-wrap gap-2 p-4 bg-muted/30 rounded-lg">
        <div className="text-sm font-medium text-muted-foreground mb-2 w-full">Sort by:</div>
        <SortButton field="occ_title">Occupation</SortButton>
        <SortButton field="median_salary">Median</SortButton>
        <SortButton field="mean_salary">Mean</SortButton>
        <SortButton field="employment">Jobs</SortButton>
        <SortButton field="soc_code">SOC Code</SortButton>
      </div>

      {paginatedData.length === 0 ? (
        <Card>
          <CardContent className="p-4 sm:p-8 text-center">
            <p className="text-muted-foreground">
              No occupations found matching your filters. Try adjusting your search criteria.
            </p>
          </CardContent>
        </Card>
      ) : (
        paginatedData.map((occupation, index) => {
          const rank = (currentPage - 1) * pageSize + index + 1;
          return (
            <Card key={occupation.soc_code} className="w-full">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="outline" className="font-semibold">
                      #{rank}
                    </Badge>
                    {(() => {
                      const salary = occupation.median_salary || occupation.mean_salary || 0;
                      const profile = getEarnerProfile(salary);
                      return profile.badge && (
                        <Badge 
                          variant={profile.badge.includes('Top 1%') ? 'default' : 'secondary'} 
                          className="text-xs"
                        >
                          {profile.badge}
                        </Badge>
                      );
                    })()}
                    <OccupationLink
                      occupation={{
                        occ_title: occupation.occ_title,
                        soc_code: occupation.soc_code,
                        slug: occupation.slug
                      }}
                      className="font-semibold hover:text-primary/80 text-base leading-tight"
                    >
                      {occupation.occ_title}
                    </OccupationLink>
                  </div>
                </div>
                <div className="space-y-1 text-xs text-muted-foreground">
                  <div className="break-words">
                    <Link 
                      to={`/what-is-soc-code?example=${occupation.soc_code}`}
                      className="text-muted-foreground hover:text-primary transition-colors"
                      title={`Learn about SOC code ${occupation.soc_code}`}
                    >
                      SOC: {occupation.soc_code}
                    </Link>
                  </div>
                  {occupation.no_degree_required && (
                    <div>
                      <Badge variant="secondary" className="text-xs">
                        No Degree Required
                      </Badge>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <div className="text-lg font-bold text-primary whitespace-nowrap">
                      {formatCurrency(occupation.median_salary || 0)}
                    </div>
                    <div className="text-xs text-muted-foreground">Median Salary</div>
                  </div>
                  <div className="space-y-1">
                    <div className="text-lg font-semibold whitespace-nowrap">
                      {occupation.mean_salary ? formatCurrency(occupation.mean_salary) : 'N/A'}
                    </div>
                    <div className="text-xs text-muted-foreground">Mean Salary</div>
                  </div>
                  <div className="space-y-1">
                    <div className="font-medium whitespace-nowrap">
                      {formatNumber(occupation.employment || 0)}
                    </div>
                    <div className="text-xs text-muted-foreground">Employment</div>
                  </div>
                  <div className="space-y-1">
                    <div className="font-medium text-sm whitespace-nowrap">
                      {occupation.p10_salary && occupation.p90_salary
                        ? `${formatCurrency(occupation.p10_salary)} - ${formatCurrency(occupation.p90_salary)}`
                        : 'N/A'}
                    </div>
                    <div className="text-xs text-muted-foreground">Salary Range</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })
      )}
    </div>
  );

  const pageTitle = viewParam === 'highest-paying' 
    ? 'Highest Paying Jobs - Occupation Rankings | Wage Atlas'
    : viewParam === 'most-jobs'
    ? 'Most Jobs Available - Occupation Rankings | Wage Atlas'
    : 'Occupation Rankings by SOC Code | Wage Atlas';
  
  const pageDescription = viewParam === 'highest-paying'
    ? 'Browse and rank all occupations by salary. Find the highest paying jobs ranked by median and average pay, employment, and SOC code.'
    : 'Browse occupations ranked by employment opportunities, salary, and SOC code. Find the best career opportunities.';

  return (
    <>
      <SeoHead 
        title={pageTitle}
        description={pageDescription}
        canonical="/rankings/"
      />
      
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          {/* Mobile Banner Ad */}
          <AdFrame className="mb-6 md:hidden">
            <MobileBannerAd />
          </AdFrame>

          {/* Header */}
          <div className="mb-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
              <div>
                <h1 className="text-3xl md:text-4xl font-bold mb-2">
                  Occupation Rankings
                </h1>
                <p className="text-muted-foreground">
                  Rank and compare all occupations by salary, employment, and SOC code
                </p>
              </div>
              
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={handleShare}>
                  <Share2 className="h-4 w-4 mr-2" />
                  Share
                </Button>
                <CSVExportButton 
                  area="United States"
                  type="rankings"
                  year={2025}
                />
              </div>
            </div>

            {/* View Selector */}
            <div className="flex flex-wrap gap-2 mb-4">
              <Button
                variant={viewParam === 'highest-paying' ? 'default' : 'outline'}
                size="sm"
                onClick={() => handleViewChange('highest-paying')}
              >
                <DollarSign className="h-4 w-4 mr-2" />
                Highest Paying
              </Button>
              <Button
                variant={viewParam === 'most-jobs' ? 'default' : 'outline'}
                size="sm"
                onClick={() => handleViewChange('most-jobs')}
              >
                <Users className="h-4 w-4 mr-2" />
                Most Jobs
              </Button>
              <Button
                variant={viewParam === 'alphabetical' ? 'default' : 'outline'}
                size="sm"
                onClick={() => handleViewChange('alphabetical')}
              >
                Alphabetical
              </Button>
              <Button
                variant={viewParam === 'soc-code' ? 'default' : 'outline'}
                size="sm"
                onClick={() => handleViewChange('soc-code')}
              >
                SOC Code
              </Button>
            </div>

            {/* Filters Toggle */}
            <div className="flex items-center gap-4 mb-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filter className="h-4 w-4 mr-2" />
                Filters
                {(socPrefix || minSalary || maxSalary || minEmployment || noDegree || earnerTier) && (
                  <Badge variant="secondary" className="ml-2">
                    {[socPrefix, minSalary, maxSalary, minEmployment, noDegree, earnerTier].filter(Boolean).length}
                  </Badge>
                )}
              </Button>
              
              {(socPrefix || minSalary || maxSalary || minEmployment || noDegree || earnerTier) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                >
                  <X className="h-4 w-4 mr-2" />
                  Clear Filters
                </Button>
              )}
            </div>

            {/* Filters Panel */}
            {showFilters && (
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle className="text-lg">Filters</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <Label htmlFor="soc-prefix">SOC Code Prefix</Label>
                      <Input
                        id="soc-prefix"
                        placeholder="e.g., 11-"
                        value={socPrefix}
                        onChange={(e) => updateParams({ soc: e.target.value || null })}
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Filter by SOC code (e.g., 11- for Management)
                      </p>
                    </div>
                    
                    <div>
                      <Label htmlFor="min-salary">Min Salary</Label>
                      <Input
                        id="min-salary"
                        type="number"
                        placeholder="e.g., 50000"
                        value={minSalary}
                        onChange={(e) => updateParams({ min_salary: e.target.value || null })}
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="max-salary">Max Salary</Label>
                      <Input
                        id="max-salary"
                        type="number"
                        placeholder="e.g., 200000"
                        value={maxSalary}
                        onChange={(e) => updateParams({ max_salary: e.target.value || null })}
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="min-employment">Min Employment</Label>
                      <Input
                        id="min-employment"
                        type="number"
                        placeholder="e.g., 10000"
                        value={minEmployment}
                        onChange={(e) => updateParams({ min_employment: e.target.value || null })}
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="earner-tier">Earner Tier</Label>
                      <Select 
                        value={earnerTier || 'all'} 
                        onValueChange={(value) => updateParams({ earner_tier: value === 'all' ? null : value })}
                      >
                        <SelectTrigger id="earner-tier">
                          <SelectValue placeholder="All Tiers" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Tiers</SelectItem>
                          <SelectItem value="elite">Elite Earner (Top 5%)</SelectItem>
                          <SelectItem value="high">High Earner (Top 25%)</SelectItem>
                          <SelectItem value="above">Above Average</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="flex items-center space-x-2 pt-6">
                      <Checkbox
                        id="no-degree"
                        checked={noDegree}
                        onCheckedChange={(checked) => updateParams({ no_degree: checked ? 'true' : null })}
                      />
                      <Label htmlFor="no-degree" className="cursor-pointer">
                        No Degree Required
                      </Label>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Results Count */}
            <div className="text-sm text-muted-foreground mb-4">
              Showing {paginatedData.length} of {filteredAndSortedData.length} occupations
              {socPrefix && ` (filtered by SOC: ${socPrefix})`}
            </div>
          </div>

          {/* Leaderboard Ad */}
          <AdFrame className="hidden md:block mb-6">
            <LeaderboardAd />
          </AdFrame>

          {/* Main Content */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Main Column */}
            <div className="lg:col-span-3">
              {isLoading && (
                <div className="space-y-4">
                  {[...Array(10)].map((_, i) => (
                    <Skeleton key={i} className="h-16 w-full" />
                  ))}
                </div>
              )}

              {error && (
                <Card>
                  <CardContent className="text-center">
                    <p className="text-muted-foreground">Error loading rankings. Please try again.</p>
                  </CardContent>
                </Card>
              )}

              {!isLoading && !error && (
                <>
                  {/* Mobile: card layout, no sidebar */}
                  <div className="md:hidden">
                    <MobileCardLayout />
                  </div>

                  {/* Desktop / Tablet: table layout */}
                  <div className="hidden md:block">
                    {/* Rankings Table - Desktop */}
                    <Card>
                      <CardContent className="p-0">
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm md:text-base">
                            <colgroup>
                              <col style={{ width: '60px' }} />   {/* Rank */}
                              <col style={{ minWidth: '260px' }} /> {/* Occupation */}
                              <col style={{ width: '130px' }} />  {/* Median */}
                              <col style={{ width: '130px' }} />  {/* Mean */}
                              <col style={{ width: '120px' }} />  {/* Jobs */}
                              <col style={{ width: '140px' }} />  {/* Earner Profile */}
                              <col style={{ width: '220px' }} /> {/* Salary Range */}
                            </colgroup>
                            <thead>
                              <tr className="border-b bg-muted/50">
                                <th className="text-left p-3 font-semibold whitespace-nowrap">
                                  Rank
                                </th>
                                <th className="text-left p-3 font-semibold whitespace-nowrap">
                                  <SortButton field="occ_title">Occupation</SortButton>
                                </th>
                                <th className="text-right p-3 font-semibold whitespace-nowrap">
                                  <SortButton field="median_salary">Median</SortButton>
                                </th>
                                <th className="text-right p-3 font-semibold whitespace-nowrap">
                                  <SortButton field="mean_salary">Mean</SortButton>
                                </th>
                                <th className="text-right p-3 pr-4 font-semibold whitespace-nowrap">
                                  <SortButton field="employment">Jobs</SortButton>
                                </th>
                                <th className="text-center p-3 px-4 font-semibold whitespace-nowrap">
                                  Earner Profile
                                </th>
                                <th className="text-right p-3 pr-4 font-semibold hidden md:table-cell whitespace-nowrap">
                                  <span>Salary Range</span>
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {paginatedData.length === 0 ? (
                                <tr>
                                  <td colSpan={7} className="p-8 text-center text-muted-foreground">
                                    No occupations found matching your filters. Try adjusting your search criteria.
                                  </td>
                                </tr>
                              ) : (
                                paginatedData.map((occupation, index) => {
                                  const rank = (currentPage - 1) * pageSize + index + 1;
                                  return (
                                    <tr key={occupation.soc_code} className="border-b hover:bg-muted/30">
                                      <td className="p-3 font-semibold text-muted-foreground text-xs md:text-sm whitespace-nowrap">
                                        #{rank}
                                      </td>
                                      <td className="p-3 whitespace-normal">
                                        <div className="flex flex-col gap-1">
                                          <OccupationLink
                                            occupation={{
                                              occ_title: occupation.occ_title,
                                              soc_code: occupation.soc_code,
                                              slug: occupation.slug
                                            }}
                                            className="font-medium hover:text-primary/80 text-sm md:text-base break-words"
                                          >
                                            {occupation.occ_title}
                                          </OccupationLink>
                                          <div className="flex items-center gap-2 flex-wrap">
                                            <Link 
                                              to={`/what-is-soc-code?example=${occupation.soc_code}`}
                                              className="text-xs text-muted-foreground hover:text-primary transition-colors"
                                              title={`Learn about SOC code ${occupation.soc_code}`}
                                            >
                                              SOC: {occupation.soc_code}
                                            </Link>
                                            {occupation.no_degree_required && (
                                              <Badge variant="secondary" className="text-xs">
                                                No Degree
                                              </Badge>
                                            )}
                                          </div>
                                        </div>
                                      </td>
                                      <td className="p-3 text-right font-semibold text-xs md:text-sm whitespace-nowrap">
                                        {formatCurrency(occupation.median_salary || 0)}
                                      </td>
                                      <td className="p-3 text-right text-xs md:text-sm whitespace-nowrap">
                                        {occupation.mean_salary ? formatCurrency(occupation.mean_salary) : 'N/A'}
                                      </td>
                                      <td className="p-3 pr-4 text-right text-muted-foreground text-xs md:text-sm whitespace-nowrap">
                                        {formatNumber(occupation.employment || 0)}
                                      </td>
                                      <td className="p-3 px-4 text-center">
                                        {(() => {
                                          const salary = occupation.median_salary || occupation.mean_salary || 0;
                                          const profile = getEarnerProfile(salary);
                                          return profile.badge ? (
                                            <TooltipProvider>
                                              <Tooltip>
                                                <TooltipTrigger asChild>
                                                  <Badge 
                                                    variant={profile.badge.includes('Top 1%') ? 'default' : 'secondary'} 
                                                    className="text-xs cursor-help"
                                                  >
                                                    {profile.badge}
                                                  </Badge>
                                                </TooltipTrigger>
                                                <TooltipContent>
                                                  <p className="font-semibold">{profile.tier}</p>
                                                  <p className="text-xs">Top {profile.percentile}% of US workers</p>
                                                </TooltipContent>
                                              </Tooltip>
                                            </TooltipProvider>
                                          ) : (
                                            <span className="text-xs text-muted-foreground">{profile.tier}</span>
                                          );
                                        })()}
                                      </td>
                                      <td className="p-3 pr-4 text-right text-xs text-muted-foreground hidden md:table-cell whitespace-nowrap">
                                        {occupation.p10_salary && occupation.p90_salary
                                          ? `${formatCurrency(occupation.p10_salary)} - ${formatCurrency(occupation.p90_salary)}`
                                          : 'N/A'}
                                      </td>
                                    </tr>
                                  );
                                })
                              )}
                            </tbody>
                          </table>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="flex justify-center mt-6">
                        <Pagination>
                          <PaginationContent>
                            <PaginationItem>
                              <PaginationPrevious 
                                href="#" 
                                onClick={(e) => {
                                  e.preventDefault();
                                  if (currentPage > 1) handlePageChange(currentPage - 1);
                                }}
                                className={currentPage <= 1 ? 'pointer-events-none opacity-50' : ''}
                              />
                            </PaginationItem>
                            
                            {[...Array(Math.min(5, totalPages))].map((_, i) => {
                              const pageNum = Math.max(1, currentPage - 2) + i;
                              if (pageNum > totalPages) return null;
                              
                              return (
                                <PaginationItem key={pageNum}>
                                  <PaginationLink
                                    href="#"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      handlePageChange(pageNum);
                                    }}
                                    isActive={pageNum === currentPage}
                                  >
                                    {pageNum}
                                  </PaginationLink>
                                </PaginationItem>
                              );
                            })}
                            
                            {totalPages > 5 && currentPage < totalPages - 2 && (
                              <PaginationItem>
                                <PaginationEllipsis />
                              </PaginationItem>
                            )}
                            
                            <PaginationItem>
                              <PaginationNext 
                                href="#" 
                                onClick={(e) => {
                                  e.preventDefault();
                                  if (currentPage < totalPages) handlePageChange(currentPage + 1);
                                }}
                                className={currentPage >= totalPages ? 'pointer-events-none opacity-50' : ''}
                              />
                            </PaginationItem>
                          </PaginationContent>
                        </Pagination>
                      </div>
                    )}

                    {/* In-Content Ad */}
                    <InContentAd />
                  </div>
                </>
              )}
            </div>

            {/* Sidebar - desktop only (no mobile) */}
            <div className="hidden lg:block space-y-6">
              <MetricNavigation
                currentMetric="rankings"
                collapsible={false}
              />
              
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">About Rankings</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Rankings are based on the latest Bureau of Labor Statistics (BLS) data. 
                    All salaries are annual wages for the United States.
                  </p>
                  
                  <div>
                    <h4 className="text-sm font-semibold mb-2">Understanding SOC Codes</h4>
                    <p className="text-xs text-muted-foreground mb-2">
                      SOC (Standard Occupational Classification) codes organize occupations into categories.
                    </p>
                    <Button variant="link" size="sm" className="p-0 h-auto" asChild>
                      <a href="/what-is-soc-code">Learn more about SOC codes →</a>
                    </Button>
                  </div>
                  
                  <Separator />
                  
                  <div>
                    <h4 className="text-sm font-semibold mb-2">What's Included</h4>
                    <ul className="text-xs text-muted-foreground space-y-1">
                      <li>• Salary percentile ranges (P10-P90)</li>
                      <li>• Historical ranking trends</li>
                      <li>• Advanced filtering options</li>
                    </ul>
                    <p className="text-xs text-muted-foreground mt-2">
                      All rankings and salary data are free to explore - no account required.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default OccupationRankings;
